import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import RightClickMenu, { MenuItem } from '../general-components/right-click-menu';
import * as SessionActions from '../../stores/actions/session';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../stores/types/tabs';
import { setTemporaryEditedUser, setTemporaryEditedUserWebRTCId } from '../../stores/actions/user-interface';
import { setMetaMenuRoute, setMetaMenuState } from '../../stores/actions/tabs';
import { ICONS } from '../general-components/icon';

const filterHidden = (i: MenuItem) => !i.hidden;
interface Props {
	clickCoordinates?: [number, number];
	contextMenuId: string;
	socket: Socket | null;
	userId: number;
	webRTCId?: string;
	kickUser?: () => void;
}

export default function VideoFeedContextMenu(props: Props) {
	const dispatch = useDispatch();

	const {
		clickCoordinates,
		contextMenuId,
		userId,
		webRTCId,
		kickUser,
	} = props;
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const {
		onsiteUsers,
		advancedAudioUsers,
		r1080pUsers,
	} = useSelector((s: GS) => s.SessionReducer.userMeetingSettings);

	const isUserInList = (list?: number[]) => {
		if (userId && list?.includes(userId)) {
			return true;
		}
		return false;
	};

	const handleToggleSetting = (settingKey: string, currentValue: boolean) => {
		if (!token || !meeting) return;
		const settings = { [settingKey]: !currentValue };
		dispatch(SessionActions.updateUserMeetingSettings(token, meeting.meeting, userId, settings));
	};

	const openEditProfileOtherUser = () => {
		dispatch(setTemporaryEditedUser(userId));
		dispatch(setTemporaryEditedUserWebRTCId(webRTCId));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.PROFILE_EDIT));
	};

	const producerFeed = useMemo(() => (
		connectedUsers.find((user) => user.gr_user === userId)?.isProducer
	), [userId, connectedUsers]);

	const showEdit = useMemo(
		() => host && !producerFeed,
		[host, producerFeed],
	);

	const isOnsiteUser = useMemo(() => isUserInList(onsiteUsers), [onsiteUsers, userId]);
	const isAdvancedAudioUser = useMemo(() => isUserInList(advancedAudioUsers), [advancedAudioUsers, userId]);
	const is1080pUser = useMemo(() => isUserInList(r1080pUsers), [r1080pUsers, userId]);

	const menuItems: MenuItem[] = useMemo(() => {
		if (host && !!kickUser) {
			return [{
				text: 'Kick SIP Feed',
				handleClick: kickUser,
			}];
		}

		const items: MenuItem[] = [
			{
				handleClick: openEditProfileOtherUser,
				text: 'Edit Profile',
				hidden: !showEdit,
				iconName: ICONS.PERSON,
				iconSize: 12,
			},
			{
				checkboxValue: isOnsiteUser,
				handleClick: () => handleToggleSetting('onsite_user', isOnsiteUser),
				text: 'Onsite',
				dontClose: true,
			},
			{
				checkboxValue: isAdvancedAudioUser,
				handleClick: () => handleToggleSetting('advanced_audio_user', isAdvancedAudioUser),
				text: 'Advanced Audio',
				dontClose: true,
			},
			{
				checkboxValue: is1080pUser,
				handleClick: () => handleToggleSetting('r1080p_user', is1080pUser),
				text: 'Enable 1080p',
				dontClose: true,
			},
		];

		return items;
	}, [isOnsiteUser, isAdvancedAudioUser, is1080pUser, userId, showEdit]);

	const nClickCoordinates = useMemo((): [number, number] | undefined => {
		if (!clickCoordinates) return clickCoordinates;
		const shownItems = menuItems.filter(filterHidden);
		const newY = 36 * (shownItems.length);
		return [clickCoordinates[0] - 4, clickCoordinates[1] + newY];
	}, [clickCoordinates, menuItems]);

	return <RightClickMenu
		contextMenuId={contextMenuId}
		items={menuItems}
		clickCoordinates={nClickCoordinates}
	/>;
}
